/**
 * @fileoverview Firebase Friends, Game Invites, and Session Messaging Service
 *
 * Handles all social features: friend requests, friend list management,
 * direct game invites, and session-based messaging.
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  addDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { containsInappropriateContent } from '../../utils/profanityFilter';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Friendship {
  id: string;             // Document ID (sortedUid pair)
  users: string[];        // [uid1, uid2] sorted
  status: 'pending' | 'accepted';
  requestedBy: string;    // UID of sender
  requestedAt: Date;
  acceptedAt?: Date;
  lastMessageBy?: string; // UID of last message sender
  lastMessageAt?: Date;   // When last message was sent
  // Denormalized user info for display without extra reads
  userProfiles: {
    [uid: string]: {
      username: string;
      friendTag: string;
      zodiacResult?: string;
    };
  };
}

export interface GameInvite {
  id: string;
  fromUid: string;
  fromUsername: string;
  fromFriendTag: string;
  toUid: string;
  gameId: string;
  timeControl: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  createdAt: Date;
}

export interface SessionMessage {
  id: string;
  fromUid: string;
  text: string;
  timestamp: Date;
}

export interface UserSearchResult {
  uid: string;
  username: string;
  friendTag: string;
  zodiacResult?: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const FRIENDSHIPS_COLLECTION = 'friendships';
const GAME_INVITES_COLLECTION = 'game_invites';
const SESSION_MESSAGES_COLLECTION = 'session_messages';
const USERS_COLLECTION = 'users';
const INVITE_EXPIRY_MS = 60000; // 60 seconds

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Create a deterministic friendship doc ID from two UIDs */
function getFriendshipId(uid1: string, uid2: string): string {
  return uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
}

/** Convert Firestore timestamp to Date */
function toDate(val: any): Date {
  if (!val) return new Date();
  if (val.toDate) return val.toDate();
  if (val instanceof Date) return val;
  return new Date(val);
}

// ─── Friend Requests ────────────────────────────────────────────────────────

export class FriendsService {

  /**
   * Search for a user by username#tag
   */
  static async searchUser(usernameWithTag: string): Promise<UserSearchResult | null> {
    const parts = usernameWithTag.split('#');
    if (parts.length !== 2) return null;

    const username = parts[0].trim();
    const tag = parts[1].trim();

    if (!username || !tag || tag.length !== 3) return null;

    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where('profile.username', '==', username));
    const snapshot = await getDocs(q);

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const profile = data.profile;
      if (profile?.friendTag === tag) {
        return {
          uid: docSnap.id,
          username: profile.username,
          friendTag: profile.friendTag,
          zodiacResult: profile.zodiacResult,
        };
      }
    }

    return null;
  }

  /**
   * Send a friend request
   */
  static async sendFriendRequest(
    fromUid: string,
    toUid: string,
    fromProfile: { username: string; friendTag: string; zodiacResult?: string },
    toProfile: { username: string; friendTag: string; zodiacResult?: string }
  ): Promise<{ success: boolean; error?: string }> {
    if (fromUid === toUid) {
      return { success: false, error: 'Cannot add yourself' };
    }

    const friendshipId = getFriendshipId(fromUid, toUid);
    const docRef = doc(db, FRIENDSHIPS_COLLECTION, friendshipId);

    // Check if friendship already exists
    const existing = await getDoc(docRef);
    if (existing.exists()) {
      const data = existing.data();
      if (data.status === 'accepted') {
        return { success: false, error: 'Already friends' };
      }
      if (data.status === 'pending') {
        if (data.requestedBy === fromUid) {
          return { success: false, error: 'Request already sent' };
        }
        // Other person already sent us a request - auto-accept
        await updateDoc(docRef, {
          status: 'accepted',
          acceptedAt: Timestamp.now(),
        });
        return { success: true };
      }
    }

    const fromData: Record<string, string> = {
      username: fromProfile.username,
      friendTag: fromProfile.friendTag || '000',
    };
    if (fromProfile.zodiacResult !== undefined && fromProfile.zodiacResult !== null) {
      fromData.zodiacResult = fromProfile.zodiacResult;
    }

    const toData: Record<string, string> = {
      username: toProfile.username,
      friendTag: toProfile.friendTag || '000',
    };
    if (toProfile.zodiacResult !== undefined && toProfile.zodiacResult !== null) {
      toData.zodiacResult = toProfile.zodiacResult;
    }

    await setDoc(docRef, {
      users: [fromUid, toUid].sort(),
      status: 'pending',
      requestedBy: fromUid,
      requestedAt: Timestamp.now(),
      userProfiles: {
        [fromUid]: fromData,
        [toUid]: toData,
      },
    });

    return { success: true };
  }

  /**
   * Accept a friend request
   */
  static async acceptFriendRequest(friendshipId: string): Promise<{ success: boolean; error?: string }> {
    const docRef = doc(db, FRIENDSHIPS_COLLECTION, friendshipId);
    const snap = await getDoc(docRef);

    if (!snap.exists()) {
      return { success: false, error: 'Request not found' };
    }

    await updateDoc(docRef, {
      status: 'accepted',
      acceptedAt: Timestamp.now(),
    });

    return { success: true };
  }

  /**
   * Decline a friend request or remove a friend
   */
  static async removeFriendship(friendshipId: string): Promise<void> {
    const docRef = doc(db, FRIENDSHIPS_COLLECTION, friendshipId);
    await deleteDoc(docRef);
  }

  /**
   * Subscribe to all friendships for a user (real-time)
   */
  static subscribeFriendships(
    uid: string,
    callback: (friendships: Friendship[]) => void
  ): () => void {
    const friendshipsRef = collection(db, FRIENDSHIPS_COLLECTION);
    const q = query(friendshipsRef, where('users', 'array-contains', uid));

    return onSnapshot(q, (snapshot) => {
      const friendships: Friendship[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        requestedAt: toDate(d.data().requestedAt),
        acceptedAt: d.data().acceptedAt ? toDate(d.data().acceptedAt) : undefined,
        lastMessageAt: d.data().lastMessageAt ? toDate(d.data().lastMessageAt) : undefined,
      })) as Friendship[];

      callback(friendships);
    }, () => {
      callback([]);
    });
  }

  // ─── Game Invites ───────────────────────────────────────────────────────

  /**
   * Send a game invite to a friend
   */
  static async sendGameInvite(
    fromUid: string,
    fromUsername: string,
    fromFriendTag: string,
    toUid: string,
    gameId: string,
    timeControl: string
  ): Promise<{ success: boolean; error?: string }> {
    const data = {
      fromUid,
      fromUsername,
      fromFriendTag,
      toUid,
      gameId,
      timeControl,
      status: 'pending',
      createdAt: Timestamp.now(),
    };

    await addDoc(collection(db, GAME_INVITES_COLLECTION), data);
    return { success: true };
  }

  /**
   * Subscribe to incoming game invites (real-time)
   */
  static subscribeGameInvites(
    uid: string,
    callback: (invites: GameInvite[]) => void
  ): () => void {
    const invitesRef = collection(db, GAME_INVITES_COLLECTION);
    const q = query(
      invitesRef,
      where('toUid', '==', uid),
      where('status', '==', 'pending')
    );

    return onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const invites: GameInvite[] = [];

      snapshot.docs.forEach((d) => {
        const data = d.data();
        const createdAt = toDate(data.createdAt);

        // Filter out expired invites (older than 60s)
        if (now - createdAt.getTime() < INVITE_EXPIRY_MS) {
          invites.push({
            id: d.id,
            fromUid: data.fromUid,
            fromUsername: data.fromUsername,
            fromFriendTag: data.fromFriendTag,
            toUid: data.toUid,
            gameId: data.gameId,
            timeControl: data.timeControl,
            status: data.status,
            createdAt,
          });
        }
      });

      callback(invites);
    }, () => {
      callback([]);
    });
  }

  /**
   * Accept a game invite
   */
  static async acceptGameInvite(inviteId: string): Promise<string> {
    const docRef = doc(db, GAME_INVITES_COLLECTION, inviteId);
    const snap = await getDoc(docRef);

    if (!snap.exists()) throw new Error('Invite not found');

    const data = snap.data();
    await updateDoc(docRef, { status: 'accepted' });
    return data.gameId;
  }

  /**
   * Decline a game invite
   */
  static async declineGameInvite(inviteId: string): Promise<void> {
    const docRef = doc(db, GAME_INVITES_COLLECTION, inviteId);
    await updateDoc(docRef, { status: 'declined' });
  }

  /**
   * Delete all invite documents for a given gameId+fromUid.
   * Used to clean up stale invites before creating a new one (e.g. sandbox
   * sessions reuse the same deterministic gameId across invitations).
   */
  static async deleteInvitesForGame(gameId: string, fromUid: string): Promise<void> {
    const invitesRef = collection(db, GAME_INVITES_COLLECTION);
    const q = query(invitesRef, where('gameId', '==', gameId), where('fromUid', '==', fromUid));
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
  }

  /**
   * Subscribe to a sent invite's status (for the host to detect accept/decline)
   */
  static subscribeSentInviteStatus(
    gameId: string,
    fromUid: string,
    onDeclined: () => void,
    onAccepted?: () => void
  ): () => void {
    const invitesRef = collection(db, GAME_INVITES_COLLECTION);
    const q = query(
      invitesRef,
      where('gameId', '==', gameId),
      where('fromUid', '==', fromUid)
    );

    return onSnapshot(q, (snapshot) => {
      for (const d of snapshot.docs) {
        const status = d.data().status;
        if (status === 'declined') {
          onDeclined();
          return;
        }
        if (status === 'accepted' && onAccepted) {
          onAccepted();
          return;
        }
      }
    }, () => {});
  }

  // ─── Session Messages ───────────────────────────────────────────────────

  /**
   * Send a session message
   */
  static async sendMessage(
    conversationId: string,
    fromUid: string,
    text: string
  ): Promise<void> {
    if (!text.trim()) return;

    const safeText = containsInappropriateContent(text) ? '[filtered]' : text.trim();

    const messagesRef = collection(db, SESSION_MESSAGES_COLLECTION, conversationId, 'messages');
    await addDoc(messagesRef, {
      fromUid,
      text: safeText,
      timestamp: serverTimestamp(),
    });

    // Stamp the friendship doc so the recipient sees an unread badge
    const friendshipRef = doc(db, FRIENDSHIPS_COLLECTION, conversationId);
    updateDoc(friendshipRef, {
      lastMessageBy: fromUid,
      lastMessageAt: serverTimestamp(),
    }).catch(() => {});
  }

  /**
   * Subscribe to messages in a conversation (real-time)
   */
  static subscribeMessages(
    conversationId: string,
    callback: (messages: SessionMessage[]) => void
  ): () => void {
    const messagesRef = collection(db, SESSION_MESSAGES_COLLECTION, conversationId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));

    return onSnapshot(q, (snapshot) => {
      const messages: SessionMessage[] = snapshot.docs.map((d) => ({
        id: d.id,
        fromUid: d.data().fromUid,
        text: d.data().text,
        timestamp: toDate(d.data().timestamp),
      }));
      callback(messages);
    }, () => {
      callback([]);
    });
  }

  /**
   * Get the conversation ID for two users
   */
  static getConversationId(uid1: string, uid2: string): string {
    return getFriendshipId(uid1, uid2);
  }
}
